import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Card,
  Table,
  Space,
  Modal,
  Button,
  Progress,
  Tooltip,
} from "antd";
import {
  LoadingOutlined,
  DownloadOutlined,
  TeamOutlined,
  BankOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import { exportMultiSectionCSV, exportToCSV } from "../../Utilities";
import styles from "./AdminAICharts.module.css";
import FreeTrialsChart from "./FreeTrialsChart";
import ConversionsChart from "./ConversionsChart";
import ImageGenerationsChart from "./ImageGenerationsChart";
import NewSignupsChart from "./NewSignupsChart";
import ActiveAIStats from "./ActiveAIStats";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import useTableFilter from "../../UseTableFilter/useTableFilter";

const { Title, Text } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

function AdminAICharts() {
  const chartStyle = useMemo(() => ({
    padding: 24,
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #f0f0f0",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03)",
  }), []);

  const [dateRange, setDateRange] = useState({
    start: moment().subtract(6, 'days'),
    end: moment()
  });

  const [getColumnSearchProps] = useTableFilter();

  const handleDateChange = (dateValues) => {
    if (dateValues && dateValues[0] && dateValues[1]) {
      setDateRange({
        start: dateValues[0],
        end: dateValues[1]
      });
    }
  };

  // State for subscription data
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal and User list state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [allUsersData, setAllUsersData] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const [isExportLoading, setIsExportLoading] = useState(false);

  // Companies state
  const [companiesData, setCompaniesData] = useState([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [selectedCompanySubscription, setSelectedCompanySubscription] = useState(null);
  const [companyDetailsList, setCompanyDetailsList] = useState([]);

  // Fetch active users data
  const fetchActiveUsersData = async () => {
    try {
      setIsLoading(true);
      const payload = {
        action: "get_active_users_count"
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Transform API data to table format
      const transformedData = data.map((item, index) => ({
        key: index.toString(),
        subscription: item.type_name,
        activeUsers: parseInt(item.count, 10),
      }));

      setSubscriptionData(transformedData);
    } catch (error) {
      console.error("Error fetching active users data:", error);
      setSubscriptionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all users for all subscriptions once
  const fetchAllUsersData = async () => {
    try {
      setIsUsersLoading(true);
      const payload = {
        action: 'get_users_filter_by_subscription_type'
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      setAllUsersData(data);
    } catch (error) {
      console.error("Error fetching all subscription users:", error);
      setAllUsersData([]);
    } finally {
      setIsUsersLoading(false);
    }
  };

  const handleCellClick = (record) => {
    setSelectedSubscription(record.subscription);
    setIsModalOpen(true);
    
    const filtered = allUsersData.filter(user => 
      user.subscription_name === record.subscription
    );
    setUsersList(filtered);
  };

  // Fetch companies data
  const fetchCompaniesData = async () => {
    try {
      setIsCompaniesLoading(true);
      const payload = {
        action: "get_companies_by_subscription"
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      const transformedData = data.map((item, index) => ({
        key: index.toString(),
        subscription: item.type_name,
        count: parseInt(item.count, 10),
        details: item.details || [],
      }));

      setCompaniesData(transformedData);
    } catch (error) {
      console.error("Error fetching companies data:", error);
      setCompaniesData([]);
    } finally {
      setIsCompaniesLoading(false);
    }
  };

  const handleCompanyCellClick = (record) => {
    setSelectedCompanySubscription(record.subscription);
    setCompanyDetailsList(record.details);
    setIsCompanyModalOpen(true);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchActiveUsersData();
    fetchAllUsersData();
    fetchCompaniesData();
  }, []);

  const handleExportCSV = async () => {
    try {
      setIsExportLoading(true);
      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: 'get_analytics_csv_export',
      });
      const { subscriptions = [], contracts = [], expired = [] } = response.data || {};
      exportMultiSectionCSV('analytics-export', [
        {
          title: 'Active Subscriptions',
          columns: [
            { title: 'Company Name',  dataIndex: 'company_name' },
            { title: 'Subscription',  dataIndex: 'subscription' },
            { title: 'Billing Cycle', dataIndex: 'billing_cycle' },
            { title: 'Price',         dataIndex: 'price' },
          ],
          data: subscriptions,
        },
        {
          title: 'Active Contracts',
          columns: [
            { title: 'Company Name',     dataIndex: 'company_name' },
            { title: 'Contract Type',    dataIndex: 'contract_type' },
            { title: 'Payment Schedule', dataIndex: 'payment_schedule' },
            { title: 'Price',            dataIndex: 'price' },
          ],
          data: contracts,
        },
        {
          title: 'Expired Subscriptions',
          columns: [
            { title: 'Company Name', dataIndex: 'display_name' },
            { title: 'Subscription', dataIndex: 'subscription_name' },
            { title: 'Expired On',   dataIndex: 'expired_on' },
          ],
          data: expired,
        },
      ]);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleUsersModalExport = () => {
    const enriched = usersList.map((row) => {
      const used = Number(row.used_usage) || 0;
      const available = Number(row.available_usage) || 0;
      const total = used + available;
      const percent = total > 0 ? Math.round((used / total) * 100) : 0;
      return {
        ...row,
        used_credits:  used,
        total_credits: total,
        usage_percent: total > 0 ? `${percent}%` : '',
      };
    });

    const cols = [
      { title: 'Username',                  dataIndex: 'username' },
      { title: 'Email',                     dataIndex: 'email' },
      { title: 'Profile Name',              dataIndex: 'profile_name' },
      { title: 'Company Name',              dataIndex: 'company_display_name' },
      { title: 'Used Credits',              dataIndex: 'used_credits' },
      { title: 'Total Credits',             dataIndex: 'total_credits' },
      { title: 'Usage %',                   dataIndex: 'usage_percent' },
      { title: 'Subscription Start Date',   dataIndex: 'subscription_start_time' },
      { title: 'Subscription Renewal Date', dataIndex: 'subscription_end_time' },
    ];
    const filename = `${selectedSubscription || 'subscription'}-users`;
    exportToCSV(filename, cols, enriched);
  };

  const handleCompaniesModalExport = () => {
    const isContract = selectedCompanySubscription === 'Contract';
    const enriched = companyDetailsList.map((row) => {
      const used = Number(row.used_usage) || 0;
      const available = Number(row.available_usage) || 0;
      const total = used + available;
      const percent = total > 0 ? Math.round((used / total) * 100) : 0;
      return {
        ...row,
        used_credits:  used,
        total_credits: total,
        usage_percent: total > 0 ? `${percent}%` : '',
      };
    });

    const cols = [
      { title: 'Company ID',   dataIndex: 'company_id' },
      { title: 'Company Name', dataIndex: 'display_name' },
      ...(isContract
        ? [
            { title: 'Contract Type',    dataIndex: 'contract_type' },
            { title: 'Payment Schedule', dataIndex: 'payment_schedule' },
          ]
        : [
            { title: 'Billing Cycle', dataIndex: 'billing_cycle' },
          ]),
      { title: 'Used Credits',              dataIndex: 'used_credits' },
      { title: 'Total Credits',             dataIndex: 'total_credits' },
      { title: 'Usage %',                   dataIndex: 'usage_percent' },
      { title: 'Subscription Start Date',   dataIndex: 'subscription_start_time' },
      { title: 'Subscription Renewal Date', dataIndex: 'subscription_end_time' },
    ];
    const filename = `${selectedCompanySubscription || 'subscription'}-companies`;
    exportToCSV(filename, cols, enriched);
  };

  const columns = [
    {
      title: 'Subscription Plan',
      dataIndex: 'subscription',
      key: 'subscription',
      onCell: (record) => ({
        onClick: () => handleCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>{text}</Text>
      ),
    },
    {
      title: 'Active Users',
      dataIndex: 'activeUsers',
      key: 'activeUsers',
      align: 'center',
      onCell: (record) => ({
        onClick: () => handleCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (value) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {value.toLocaleString()}
        </Text>
      ),
    },
  ];

  const userTableColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      ...getColumnSearchProps('username'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Profile Name',
      dataIndex: 'profile_name',
      key: 'profile_name',
      ...getColumnSearchProps('profile_name'),
    },
    {
      title: 'Company Name',
      dataIndex: 'company_display_name',
      key: 'company_display_name',
      ...getColumnSearchProps('company_display_name'),
    },
    {
      title: 'Usage',
      key: 'usage',
      width: 260,
      render: (_, record) => {
        const available = Number(record.available_usage) || 0;
        const used = Number(record.used_usage) || 0;
        const total = available + used;
        if (total === 0) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        const percent = Math.round((used / total) * 100);
        const status = percent >= 90 ? 'exception' : percent >= 70 ? 'active' : 'normal';
        return (
          <Tooltip title={`${used.toLocaleString()} / ${total.toLocaleString()} credits used`}>
            <div style={{ minWidth: 220 }}>
              <Progress percent={percent} size="small" status={status} format={(p) => `${p}%`} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Subscription Start Date',
      dataIndex: 'subscription_start_time',
      key: 'subscription_start_time',
      render: (text) => text,
    },
    {
      title: 'Subscription Renewal Date',
      dataIndex: 'subscription_end_time',
      key: 'subscription_end_time',
      render: (text) => text,
    }
  ];

  const companyColumns = [
    {
      title: 'Subscription Plan',
      dataIndex: 'subscription',
      key: 'subscription',
      onCell: (record) => ({
        onClick: () => handleCompanyCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>{text}</Text>
      ),
    },
    {
      title: 'Active Companies',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
      onCell: (record) => ({
        onClick: () => handleCompanyCellClick(record),
        style: { cursor: 'pointer' }
      }),
      render: (value) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {value.toLocaleString()}
        </Text>
      ),
    },
  ];

  const companyDetailsColumns = useMemo(() => {
    const isContract = selectedCompanySubscription === 'Contract';
    return [
      {
        title: 'Company ID',
        dataIndex: 'company_id',
        key: 'company_id',
      },
      {
        title: 'Company Name',
        dataIndex: 'display_name',
        key: 'display_name',
        ...getColumnSearchProps('display_name'),
      },
      ...(isContract ? [
        {
          title: 'Contract Type',
          dataIndex: 'contract_type',
          key: 'contract_type',
        },
        {
          title: 'Payment Schedule',
          dataIndex: 'payment_schedule',
          key: 'payment_schedule',
        },
      ] : [
        {
          title: 'Billing Cycle',
          dataIndex: 'billing_cycle',
          key: 'billing_cycle',
        },
      ]),
      {
        title: 'Usage',
        key: 'usage',
        width: 260,
        render: (_, record) => {
          const available = Number(record.available_usage) || 0;
          const used = Number(record.used_usage) || 0;
          const total = available + used;
          if (total === 0) {
            return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
          }
          const percent = Math.round((used / total) * 100);
          const status = percent >= 90 ? 'exception' : percent >= 70 ? 'active' : 'normal';
          return (
            <Tooltip title={`${used.toLocaleString()} / ${total.toLocaleString()} credits used`}>
              <div style={{ minWidth: 220 }}>
                <Progress
                  percent={percent}
                  size="small"
                  status={status}
                  format={(p) => `${p}%`}
                />
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: 'Subscription Start Date',
        dataIndex: 'subscription_start_time',
        key: 'subscription_start_time',
        render: (text) => text,
      },
      {
        title: 'Subscription Renewal Date',
        dataIndex: 'subscription_end_time',
        key: 'subscription_end_time',
        render: (text) => text,
      },
    ];
  }, [selectedCompanySubscription, getColumnSearchProps]);

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card
          style={{
            ...chartStyle,
            marginBottom: 0,
            background: "linear-gradient(135deg, #f5f9ff 0%, #ffffff 60%)",
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space size={12} align="center">
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "linear-gradient(135deg, #1890ff 0%, #69b1ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 20,
                  }}
                >
                  <DashboardOutlined />
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                    Active Subscriptions Overview
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Real-time subscription and user metrics
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                ghost
                icon={<DownloadOutlined />}
                loading={isExportLoading}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Col>
          </Row>
        </Card>
      </Col>

      <Col span={12}>
        <Card style={chartStyle}>
          <Space align="center" size={10} style={{ marginBottom: 4 }}>
            <TeamOutlined style={{ fontSize: 18, color: "#1890ff" }} />
            <Title level={5} style={{ margin: 0 }}>
              Active Users by Subscription
            </Title>
          </Space>
          <Table
            columns={columns}
            dataSource={subscriptionData}
            pagination={false}
            size="middle"
            style={{ marginTop: 16 }}
            loading={{ indicator: antIcon, spinning: isLoading }}
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card style={chartStyle}>
          <Space align="center" size={10} style={{ marginBottom: 4 }}>
            <BankOutlined style={{ fontSize: 18, color: "#1890ff" }} />
            <Title level={5} style={{ margin: 0 }}>
              Active Companies by Subscription
            </Title>
          </Space>
          <Table
            columns={companyColumns}
            dataSource={companiesData}
            pagination={false}
            size="middle"
            style={{ marginTop: 16 }}
            loading={{ indicator: antIcon, spinning: isCompaniesLoading }}
          />
        </Card>
      </Col>

      <ActiveAIStats chartStyle={chartStyle} />

      <NewSignupsChart chartStyle={chartStyle} />

      <FreeTrialsChart
        chartStyle={chartStyle}
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ConversionsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ImageGenerationsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <Modal
        title={`Users having ${selectedSubscription || '-'} Subscription`}
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setUsersList([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose={true}
        centered
      >
        <Row justify="end" style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            onClick={handleUsersModalExport}
            disabled={!usersList.length}
            style={{ borderRadius: 5 }}
          >
            Export CSV
          </Button>
        </Row>
        <Table
          columns={userTableColumns}
          dataSource={usersList}
          loading={{ indicator: antIcon, spinning: isUsersLoading }}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>

      <Modal
        title={`Companies having ${selectedCompanySubscription || '-'} Subscription`}
        visible={isCompanyModalOpen}
        onCancel={() => {
          setIsCompanyModalOpen(false);
          setCompanyDetailsList([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose={true}
        centered
      >
        <Row justify="end" style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            onClick={handleCompaniesModalExport}
            disabled={!companyDetailsList.length}
            style={{ borderRadius: 5 }}
          >
            Export CSV
          </Button>
        </Row>
        <Table
          columns={companyDetailsColumns}
          dataSource={companyDetailsList}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>
    </Row>
  );
}

export default AdminAICharts;